import React, { useEffect } from 'react';
import { Col, Divider, Row } from 'antd';
import { isEqual } from 'lodash';

import {
  ContactPersonModel,
  CustomerHistoryDetailsModel,
  FullCustomerInfoResponseModel,
} from 'api/adminApiClient';
import HistoryLog from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryLog';
import HistoryField from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryField';
import { isAdminSelector } from 'redux/selectors/authenticationSelectors';
import { adminUsersNameMapSelector } from 'redux/selectors/adminSelectors';
import { getAllAdmins } from 'redux/reducers/adminSlice';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import {
  getMyBasePayTeamDisplayName,
  hasMyBasePayTeamFieldChanged,
  resolveMyBasePayTeam,
} from 'utils/myBasePayTeamUtils';
import {
  getPrimaryContactsMaxLength,
  hasAnyPrimaryContactsDisplayableChange,
  hasPrimaryContactFieldChanged,
} from 'utils/primaryContactHistoryUtils';
import {
  getContactForLabel,
  getPreferredContactMethodLabel,
} from 'utils/primaryContactUtils';

import styles from 'components/ActivitiesDetailsSection/HistorySection/HistoryList/HistoryCard/HistoryCard.module.less';

type Props = {
  historyDetails: CustomerHistoryDetailsModel;
  historyId: string;
};

const resolveCustomerGeneralInfo = (
  customerDetails?: FullCustomerInfoResponseModel,
) => customerDetails?.generalInfo?.generalInfo;

const resolveCustomerPrimaryContacts = (
  customerDetails?: FullCustomerInfoResponseModel,
): ContactPersonModel[] | undefined =>
  resolveCustomerGeneralInfo(customerDetails)?.primaryContacts;

const resolveCustomerMyBasePayTeam = (
  customerDetails?: FullCustomerInfoResponseModel,
) => resolveMyBasePayTeam(resolveCustomerGeneralInfo(customerDetails));

export default function GeneralInformationDetailsCard(props: Props) {
  const { historyDetails, historyId } = props;
  const { oldData, newData } = historyDetails;
  const dispatch = useAppDispatch();
  const isAdmin = useAppSelector(isAdminSelector);
  const adminNamesMap = useAppSelector(adminUsersNameMapSelector);

  useEffect(() => {
    if (isAdmin) {
      dispatch(getAllAdmins());
    }
  }, [dispatch, isAdmin]);

  const oldPrimaryContacts = resolveCustomerPrimaryContacts(oldData);
  const newPrimaryContacts = resolveCustomerPrimaryContacts(newData);
  const oldMyBasePayTeam = resolveCustomerMyBasePayTeam(oldData);
  const newMyBasePayTeam = resolveCustomerMyBasePayTeam(newData);

  const hasCompanyNameChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.companyName,
    newData?.generalInfo?.generalInfo?.companyInfo?.companyName,
  );
  const hasCompanyWebsiteChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.companyWebSite,
    newData?.generalInfo?.generalInfo?.companyInfo?.companyWebSite,
  );
  const hasCompanyEmailChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.companyEmail,
    newData?.generalInfo?.generalInfo?.companyInfo?.companyEmail,
  );
  const hasCompanyPhoneNumberChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.companyPhoneNumber,
    newData?.generalInfo?.generalInfo?.companyInfo?.companyPhoneNumber,
  );
  const hasCompanyCountryChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.location?.country,
    newData?.generalInfo?.generalInfo?.companyInfo?.location?.country,
  );
  const hasCompanyStateChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.location?.state ||
      oldData?.generalInfo?.generalInfo?.companyInfo?.location
        ?.internationalStateName,
    newData?.generalInfo?.generalInfo?.companyInfo?.location?.state ||
      newData?.generalInfo?.generalInfo?.companyInfo?.location
        ?.internationalStateName,
  );
  const hasCompanyCityChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.location?.city ||
      oldData?.generalInfo?.generalInfo?.companyInfo?.location
        ?.internationalCityName,
    newData?.generalInfo?.generalInfo?.companyInfo?.location?.city ||
      newData?.generalInfo?.generalInfo?.companyInfo?.location
        ?.internationalCityName,
  );
  const hasCompanyAddressChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.location?.address,
    newData?.generalInfo?.generalInfo?.companyInfo?.location?.address,
  );
  const hasCompanyZipCodeChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.location?.zipCode,
    newData?.generalInfo?.generalInfo?.companyInfo?.location?.zipCode,
  );
  const hasAdditionalInvoiceNameChanged = !isEqual(
    oldData?.generalInfo?.generalInfo?.companyInfo?.additionalInvoiceName,
    newData?.generalInfo?.generalInfo?.companyInfo?.additionalInvoiceName,
  );

  const hasAnyCompanyFieldChanged =
    hasCompanyNameChanged ||
    hasCompanyWebsiteChanged ||
    hasCompanyEmailChanged ||
    hasCompanyPhoneNumberChanged ||
    hasCompanyCountryChanged ||
    hasCompanyStateChanged ||
    hasCompanyCityChanged ||
    hasCompanyAddressChanged ||
    hasCompanyZipCodeChanged ||
    hasAdditionalInvoiceNameChanged;

  const hasPrimaryContactsDisplayableChange =
    hasAnyPrimaryContactsDisplayableChange(oldPrimaryContacts, newPrimaryContacts);

  const hasMbpClientSuccessPartnerChanged = hasMyBasePayTeamFieldChanged(
    oldMyBasePayTeam,
    newMyBasePayTeam,
    'mbpClientSuccessPartnerUserId',
    'mbpClientSuccessPartner',
  );
  const hasMbpBillingRepresentativeChanged = hasMyBasePayTeamFieldChanged(
    oldMyBasePayTeam,
    newMyBasePayTeam,
    'mbpBillingRepresentativeUserId',
    'mbpBillingRepresentative',
  );
  const hasMbpCollectionsRepresentativeChanged = hasMyBasePayTeamFieldChanged(
    oldMyBasePayTeam,
    newMyBasePayTeam,
    'mbpCollectionsRepresentativeUserId',
    'mbpCollectionsRepresentative',
  );
  const hasMspSalesRepresentativeChanged = hasMyBasePayTeamFieldChanged(
    oldMyBasePayTeam,
    newMyBasePayTeam,
    'mspSalesRepresentativeUserId',
    'mspSalesRepresentative',
  );
  const hasMspWorkforceSpecialistChanged = hasMyBasePayTeamFieldChanged(
    oldMyBasePayTeam,
    newMyBasePayTeam,
    'mspWorkforceSpecialistUserId',
    'mspWorkforceSpecialist',
  );

  const hasMyBasePayTeamDisplayableChange =
    hasMbpClientSuccessPartnerChanged ||
    hasMspSalesRepresentativeChanged ||
    (isAdmin &&
      (hasMbpBillingRepresentativeChanged ||
        hasMbpCollectionsRepresentativeChanged ||
        hasMspWorkforceSpecialistChanged));

  const hasAnyDisplayableChange =
    hasAnyCompanyFieldChanged ||
    hasPrimaryContactsDisplayableChange ||
    hasMyBasePayTeamDisplayableChange;

  if (!hasAnyDisplayableChange) {
    return null;
  }

  const renderGeneralInfoSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    if (!hasAnyCompanyFieldChanged) {
      return null;
    }

    const sectionName = 'general-information';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>General information</div>
          </Col>

          {hasCompanyNameChanged && (
            <HistoryField
              title="Company name"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo
                  ?.companyName
              }
              dataId={`${sectionName}-company-name-field`}
            />
          )}

          {hasAdditionalInvoiceNameChanged && (
            <HistoryField
              title="Invoice name (Legal/DBA)"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo
                  ?.additionalInvoiceName
              }
            />
          )}

          {hasCompanyWebsiteChanged && (
            <HistoryField
              title="Company website"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo
                  ?.companyWebSite
              }
              dataId={`${sectionName}-company-website-field`}
            />
          )}

          {hasCompanyCountryChanged && (
            <HistoryField
              title="Country"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.country
              }
              dataId={`${sectionName}-country-field`}
            />
          )}

          {hasCompanyStateChanged && (
            <HistoryField
              title="State"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.state ||
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.internationalStateName
              }
              dataId={`${sectionName}-state-field`}
            />
          )}

          {hasCompanyCityChanged && (
            <HistoryField
              title="City"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.city ||
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.internationalCityName
              }
              dataId={`${sectionName}-city-field`}
            />
          )}

          {hasCompanyAddressChanged && (
            <HistoryField
              title="Main address"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  ?.address
              }
              dataId={`${sectionName}-main-address-field`}
            />
          )}

          {hasCompanyZipCodeChanged && (
            <HistoryField
              title="ZIP code"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo?.location
                  .zipCode
              }
              dataId={`${sectionName}-ZIP-code-field`}
            />
          )}

          {hasCompanyEmailChanged && (
            <HistoryField
              title="Email"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo
                  ?.companyEmail
              }
              dataId={`${sectionName}-email-field`}
            />
          )}

          {hasCompanyPhoneNumberChanged && (
            <HistoryField
              title="Phone number"
              value={
                customerDetails?.generalInfo?.generalInfo?.companyInfo
                  ?.companyPhoneNumber
              }
              dataId={`${sectionName}-phone-number-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderPrimaryContactSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    if (!hasPrimaryContactsDisplayableChange) {
      return null;
    }

    const primaryContacts = resolveCustomerPrimaryContacts(customerDetails) ?? [];
    const sectionName = 'primary-contact';
    const maxLength = getPrimaryContactsMaxLength(
      oldPrimaryContacts,
      newPrimaryContacts,
    );

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>Primary contact</div>
          </Col>

          <Col xs={24}>
            {Array.from({ length: maxLength }, (_, index) => {
              const primaryContact = primaryContacts[index];
              const showContactBlock =
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'firstName',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'lastName',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'email',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'phoneNumber',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'title',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'preferredContactMethod',
                ) ||
                hasPrimaryContactFieldChanged(
                  oldPrimaryContacts,
                  newPrimaryContacts,
                  index,
                  'contactFor',
                );

              if (!showContactBlock) {
                return null;
              }

              return (
                <React.Fragment key={`${index}`}>
                  <Row gutter={[10, 10]}>
                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'firstName',
                    ) && (
                      <HistoryField
                        title="First name"
                        value={primaryContact?.firstName}
                        dataId={`${sectionName}-first-name-field-${index}`}
                      />
                    )}

                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'lastName',
                    ) && (
                      <HistoryField
                        title="Last name"
                        value={primaryContact?.lastName}
                        dataId={`${sectionName}-last-name-field-${index}`}
                      />
                    )}

                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'email',
                    ) && (
                      <HistoryField
                        title="Email"
                        value={primaryContact?.email}
                        dataId={`${sectionName}-email-field-${index}`}
                      />
                    )}

                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'phoneNumber',
                    ) && (
                      <HistoryField
                        title="Phone number"
                        value={primaryContact?.phoneNumber}
                        dataId={`${sectionName}-phone-number-field-${index}`}
                      />
                    )}

                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'title',
                    ) && (
                      <HistoryField
                        title="Title"
                        value={primaryContact?.title}
                        dataId={`${sectionName}-title-field-${index}`}
                      />
                    )}

                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'preferredContactMethod',
                    ) && (
                      <HistoryField
                        title="Preferred Contact Method"
                        value={getPreferredContactMethodLabel(
                          primaryContact?.preferredContactMethod,
                        )}
                        dataId={`${sectionName}-preferred-contact-method-field-${index}`}
                      />
                    )}

                    {hasPrimaryContactFieldChanged(
                      oldPrimaryContacts,
                      newPrimaryContacts,
                      index,
                      'contactFor',
                    ) && (
                      <HistoryField
                        title="Contact For"
                        value={getContactForLabel(primaryContact?.contactFor)}
                        dataId={`${sectionName}-contact-for-field-${index}`}
                      />
                    )}
                  </Row>

                  <Divider />
                </React.Fragment>
              );
            })}
          </Col>
        </Row>

        <Divider />
      </>
    );
  };

  const renderMyBasePayTeamSection = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    if (!hasMyBasePayTeamDisplayableChange) {
      return null;
    }

    const myBasePayTeam = resolveCustomerMyBasePayTeam(customerDetails);
    const sectionName = 'mybasepay-team';

    return (
      <>
        <Row gutter={[10, 10]}>
          <Col xs={24}>
            <div className={styles.cardTitle}>myBasePay Team</div>
          </Col>

          {isAdmin && hasMbpBillingRepresentativeChanged && (
            <HistoryField
              title="MBP Billing Representative"
              value={getMyBasePayTeamDisplayName(
                myBasePayTeam,
                'mbpBillingRepresentativeUserId',
                'mbpBillingRepresentative',
                adminNamesMap,
              )}
              dataId={`${sectionName}-mbp-billing-representative-field`}
            />
          )}

          {hasMbpClientSuccessPartnerChanged && (
            <HistoryField
              title="MBP Client Success Partner"
              value={getMyBasePayTeamDisplayName(
                myBasePayTeam,
                'mbpClientSuccessPartnerUserId',
                'mbpClientSuccessPartner',
                adminNamesMap,
              )}
              dataId={`${sectionName}-mbp-client-success-partner-field`}
            />
          )}

          {isAdmin && hasMbpCollectionsRepresentativeChanged && (
            <HistoryField
              title="MBP Collections Representative"
              value={getMyBasePayTeamDisplayName(
                myBasePayTeam,
                'mbpCollectionsRepresentativeUserId',
                'mbpCollectionsRepresentative',
                adminNamesMap,
              )}
              dataId={`${sectionName}-mbp-collections-representative-field`}
            />
          )}

          {hasMspSalesRepresentativeChanged && (
            <HistoryField
              title="MSP Sales Representative"
              value={getMyBasePayTeamDisplayName(
                myBasePayTeam,
                'mspSalesRepresentativeUserId',
                'mspSalesRepresentative',
                adminNamesMap,
              )}
              dataId={`${sectionName}-msp-sales-representative-field`}
            />
          )}

          {isAdmin && hasMspWorkforceSpecialistChanged && (
            <HistoryField
              title="MSP Workforce Specialist"
              value={getMyBasePayTeamDisplayName(
                myBasePayTeam,
                'mspWorkforceSpecialistUserId',
                'mspWorkforceSpecialist',
                adminNamesMap,
              )}
              dataId={`${sectionName}-msp-workforce-specialist-field`}
            />
          )}
        </Row>

        <Divider />
      </>
    );
  };

  const renderGeneralInformation = (
    customerDetails?: FullCustomerInfoResponseModel,
  ) => {
    return (
      <>
        {renderGeneralInfoSection(customerDetails)}
        {renderPrimaryContactSection(customerDetails)}
        {renderMyBasePayTeamSection(customerDetails)}
      </>
    );
  };

  return (
    <HistoryLog
      historyId={historyId}
      oldData={renderGeneralInformation(oldData)}
      newData={renderGeneralInformation(newData)}
    />
  );
}
